import React, { useContext, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Typography, Card, Tabs, TabsProps, Empty, Button } from 'antd'
import { UploadCloud, Edit, Save } from 'react-feather'
import { FormattedMessage, injectIntl } from 'react-intl'

import { CourseContext } from '../Course/CourseContext'
import { FlexColumn, FlexRow } from '../FlexboxReusables/FlexItems'
import { Course } from '../Course/types'
import { Base64ImageUrl } from '../Course/types'
import CourseActions from './CourseActions'
import NewCourseSettingsModal from './NewCourseSettingsModal'
import { ThemeContext } from '../../Theme/ThemeContext'
import PublishedCourseActions from './PublishedCourseActions'
import { AuthContext } from '../Auth/AuthContext'
import { imageUrl } from '../../globals'

const { Meta } = Card

const CoursesOverview: React.FC = () => {
  const [ showNewCourseSettingsModal, setShowNewCourseSettingsModal ] = useState(false)
  
  const { courseContext } = useContext(CourseContext)
  const { isDarkMode } = useContext(ThemeContext)
  const { authUser } = useContext(AuthContext)

  if (!courseContext) { // only relevant when there are no courses at all (i.e. on first login ever)
    return (
      <FlexColumn width='100%' height='90vh'>
        <NewCourseSettingsModal
          open={showNewCourseSettingsModal}
          setShowModal={setShowNewCourseSettingsModal}
          onCancel={() => setShowNewCourseSettingsModal(false)}
        />
        <FlexRow>
          <FlexColumn>
            <Empty description={
              <Typography.Text>
                <FormattedMessage id='coursesOverview.noCourses' />
              </Typography.Text>
            }
            />
            <Button
              type='primary'
              style={{ marginTop: '2em' }}
              onClick={() => setShowNewCourseSettingsModal(true)}
            >
              <FormattedMessage id='navigation.courses.createNewCourse' />
            </Button>
          </FlexColumn>
        </FlexRow>
      </FlexColumn>
    )
  }

  const { courses } = courseContext

  const drafts = courses.filter((course) => !course.isPublished)
  const unsavedCourses = courses.filter((course) => course.hasUnsavedChanges)
  const publishedCourses = courses.filter((course) => course.isPublished)

  useEffect(() => {
    selectTabIfUnsavedChanges()
  }, [ courses ])

  const selectTabIfUnsavedChanges = (): string => courses.some((course) => course.hasUnsavedChanges) ? 'unsavedCourses' : 'courses'

  const items: TabsProps['items'] = [
    {
      key: 'unsavedCourses',
      label: (
        <FlexRow>
          <Save size={20} style={{ marginRight: '0.5em' }} />
          <FormattedMessage id='coursesOverview.unsavedChanges' />
        </FlexRow>
      ),
      children: (
        <FlexRow
          justifyContent='space-evenly'
          height='100vh'
          alignItems='flex-start'
          padding='2em 0'
          flexWrap
        >
          {unsavedCourses && unsavedCourses.map((course: Course) => (
            <StyledCard
              key={course.id}
              style={{ width: 360 }}
              cover={
                <Link to={`/course/${course.id}`}>
                  <PreviewImage
                    src={`${imageUrl}/${course.id}/1.jpg` || course.pages[0].previewPicture}
                    alt={course.name}
                  />
                </Link>
              }
              actions={[
                <CourseActions
                  key={course.id}
                  courseId={course.id}
                  role={authUser.role} />
              ]}
            >
              <Link to={`/course/${course.id}`}>
                <Meta
                  title={course.name}
                  description={course.description}
                  style={{lineHeight: '1em'}}
                />
              </Link>
            </StyledCard>
          ))}
        </FlexRow>
      ),
    },
    {
      key: 'drafts',
      label: (
        <FlexRow>
          <Edit size={20} style={{marginRight: '0.5em'}}/>
          <FormattedMessage id='coursesOverview.drafts' />
        </FlexRow>
      ),
      children: (
        <FlexRow
          justifyContent='space-evenly'
          height='100vh'
          alignItems='flex-start'
          padding='2em 0'
          flexWrap
        >
          {drafts.length ? drafts.map((course: Course) => (
            <StyledCard
              key={course.id}
              style={{ width: 360 }}
              cover={
                <Link to={`/course/${course.id}`}>
                  <PreviewImage
                    src={`${imageUrl}/${course.id}/1.jpg` || course.pages[0].previewPicture}
                    alt={course.name} 
                  />
                </Link>
              }
              actions={[
                <CourseActions
                  key={course.id}
                  courseId={course.id}
                  role={authUser.role} />
              ]}
            >
              <Link to={`/course/${course.id}`}>
                <Meta
                  title={
                    <span className={isDarkMode ? "dark-mode-title" : ""}>
                      {course.name}
                    </span>
                  }
                  description={course.description}
                  style={{lineHeight: '1em', color: 'black'}}
                />
              </Link>
            </StyledCard>
          ))
            : <Empty description={<FormattedMessage id='coursesOverview.noDrafts' />} />
          }
        </FlexRow>
      ),
    },
    {
      key: 'publishedCourses',
      label: (
        <FlexRow>
          <UploadCloud size={20} style={{marginRight: '0.5em'}}/>
          <FormattedMessage id='coursesOverview.published' />
        </FlexRow>
      ),
      children: (
        <FlexRow
          justifyContent='space-evenly'
          height='100vh'
          alignItems='flex-start'
          padding='2em 0'
          flexWrap
        >
          {publishedCourses.length ? publishedCourses.map((course: Course) => (
            <StyledCard
              key={course.id}
              style={{ width: 360 }}
              cover={
                <Link to={`/course/${course.id}`}>
                  <PreviewImage
                    src={`${imageUrl}/${course.id}/1.jpg` || course.pages[0].previewPicture}
                    alt={course.name} 
                  />
                </Link>
              }
              actions={[ <PublishedCourseActions key={course.id} courseId={course.id} /> ]}
            >
              <Link to={`/course/${course.id}`}>
                <Meta
                  title={course.name}
                  description={course.description}
                  style={{lineHeight: '1em'}}
                />
              </Link>
            </StyledCard>
          ))
            : <Empty description={<FormattedMessage id='coursesOverview.noPublished' />} />
          }
        </FlexRow>
      ),
    },
  ]

  return (
    <Container>
      <FlexRow height='6em' justifyContent='flex-start'>
        <Typography.Title level={3}>
          <FormattedMessage id='coursesOverview.title' />
        </Typography.Title>
      </FlexRow>
      <Tabs defaultActiveKey={selectTabIfUnsavedChanges()} items={items} />
      <NewCourseSettingsModal
        open={showNewCourseSettingsModal}
        setShowModal={setShowNewCourseSettingsModal}
        onCancel={() => setShowNewCourseSettingsModal(false)}
      />
    </Container>
  )
}

export default injectIntl(CoursesOverview)

const Container = styled.div`
  width: 100%;
  height: 100%;
  margin: 1em;
`

const PreviewImage = styled.img<{src: Base64ImageUrl }>`
  width: 100%;
  height: 12em;
  border-radius: 4px;
  cursor: pointer;
`

export const StyledCard = styled(Card)`
  cursor: pointer;
  margin: 1em;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 1.5em;

    .ant-card-meta-title {
      font-size: 1.2em;
      height: 1.2em;
      font-weight: bold;
    }

    .ant-card-meta-description {
      font-size: 0.9em;
      color: #777;
      line-height: 1.4;
    }
  }

  .ant-card-actions {
    border-top: 1px solid #e8e8e8;

    .anticon {
      transition: color 0.2s ease-in-out;

      &:hover {
        color: #1890ff;
      }
    }
  }

  .dark-mode-title {
    color: black;
  }
`